import React, { useMemo } from "react";
import { Box, Typography, Avatar, Tooltip, alpha } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import dayjs, { Dayjs } from "dayjs";
import { statusMetaFor } from "./statusMeta";

// Working-hours window for the vertical timeline (07:00–21:00).
const DAY_START = 7 * 60;
const DAY_END = 21 * 60;
const PX_PER_MIN = 0.9;

const toMin = (t?: string): number => {
  if (!t) return DAY_START;
  const [h, m] = String(t).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const fmt = (t?: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};

const initials = (name: string) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("") || "?";

interface Ev {
  row: any;
  startMin: number;
  endMin: number;
  col: number;
  ncols: number;
}

/** Interval-graph column packing so overlapping events sit side by side. */
const layout = (rows: any[]): Ev[] => {
  const evs: Ev[] = rows
    .map((row) => ({ row, startMin: toMin(row.startTime), endMin: Math.max(toMin(row.endTime), toMin(row.startTime) + 20), col: 0, ncols: 1 }))
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const out: Ev[] = [];
  let i = 0;
  while (i < evs.length) {
    const cluster: Ev[] = [evs[i]];
    let maxEnd = evs[i].endMin;
    let j = i + 1;
    while (j < evs.length && evs[j].startMin < maxEnd) {
      cluster.push(evs[j]);
      maxEnd = Math.max(maxEnd, evs[j].endMin);
      j++;
    }
    const colEnds: number[] = [];
    cluster.forEach((ev) => {
      let c = colEnds.findIndex((end) => end <= ev.startMin);
      if (c === -1) {
        c = colEnds.length;
        colEnds.push(ev.endMin);
      } else colEnds[c] = ev.endMin;
      ev.col = c;
    });
    cluster.forEach((ev) => (ev.ncols = colEnds.length));
    out.push(...cluster);
    i = j;
  }
  return out;
};

const HourGrid: React.FC<{ showLabels?: boolean }> = ({ showLabels }) => (
  <>
    {Array.from({ length: (DAY_END - DAY_START) / 60 + 1 }, (_, i) => {
      const min = DAY_START + i * 60;
      const top = (min - DAY_START) * PX_PER_MIN;
      return (
        <Box key={i} sx={{ position: "absolute", top, left: 0, right: 0, borderTop: "1px solid rgba(15,23,42,0.06)" }}>
          {showLabels && (
            <Typography sx={{ position: "absolute", top: -8, left: 4, fontSize: 10, color: "text.disabled", bgcolor: "background.paper", px: 0.5 }}>
              {fmt(`${String(Math.floor(min / 60)).padStart(2, "0")}:00`)}
            </Typography>
          )}
        </Box>
      );
    })}
  </>
);

const EventCard: React.FC<{ ev: Ev; compact?: boolean; onClick: (e: React.MouseEvent<HTMLElement>, row: any) => void }> = ({ ev, compact, onClick }) => {
  const m = statusMetaFor(ev.row);
  const top = Math.max(0, (ev.startMin - DAY_START) * PX_PER_MIN);
  const height = Math.max(24, (ev.endMin - ev.startMin) * PX_PER_MIN - 2);
  const widthPct = 100 / ev.ncols;
  const row = ev.row;
  return (
    <Tooltip title={`${row.className || "Class"} · ${fmt(row.startTime)}–${fmt(row.endTime)} · ${row.staffName || "Unassigned"}`}>
      <Box
        onClick={(e) => onClick(e, row)}
        sx={{
          position: "absolute", top, height, left: `calc(${ev.col * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
          borderRadius: "7px", bgcolor: m.bg, borderLeft: `3px solid ${m.color}`, px: 0.75, py: 0.4, overflow: "hidden",
          cursor: "pointer", boxShadow: "0 1px 3px rgba(15,23,42,0.08)", transition: "transform .1s",
          "&:hover": { transform: "translateY(-1px)", boxShadow: "0 3px 10px rgba(15,23,42,0.18)", zIndex: 2 },
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: m.text, lineHeight: 1.2 }}>{fmt(row.startTime)}–{fmt(row.endTime)}</Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.className || "Class"}
        </Typography>
        {!compact && height > 46 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <Avatar sx={{ width: 16, height: 16, fontSize: 8 }}>{initials(row.staffName || "")}</Avatar>
            <Typography sx={{ fontSize: 9.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row.staffName || "Unassigned"}
            </Typography>
          </Box>
        )}
        {!compact && height > 66 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            {row.classMode === "Online" ? <VideocamOutlinedIcon sx={{ fontSize: 12 }} /> : <LocationOnOutlinedIcon sx={{ fontSize: 12 }} />}
            <Typography sx={{ fontSize: 9.5, color: "text.secondary" }}>{row.studentCount ?? 0} students · {m.label}</Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

const dateKey = (row: any) => {
  const raw = row?.scheduledDate ?? row?.scheduled_date ?? row?.date;
  return raw ? dayjs(raw).format("YYYY-MM-DD") : "";
};

// ── Day view ──
export const DayView: React.FC<{ rows: any[]; date: Dayjs; onEventClick: (e: React.MouseEvent<HTMLElement>, row: any) => void }> = ({ rows, date, onEventClick }) => {
  const dayRows = useMemo(() => rows.filter((r) => dateKey(r) === date.format("YYYY-MM-DD")), [rows, date]);
  const evs = useMemo(() => layout(dayRows), [dayRows]);
  const height = (DAY_END - DAY_START) * PX_PER_MIN;
  return (
    <Box sx={{ display: "flex", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "12px", overflow: "hidden" }}>
      <Box sx={{ width: 56, position: "relative", height, flexShrink: 0, bgcolor: alpha("#0f172a", 0.015) }}>
        <HourGrid showLabels />
      </Box>
      <Box sx={{ position: "relative", flexGrow: 1, height, borderLeft: "1px solid rgba(15,23,42,0.06)" }}>
        <HourGrid />
        {evs.length === 0 && (
          <Typography sx={{ position: "absolute", top: 16, left: 16, fontSize: 13, color: "text.disabled" }}>No classes on this day</Typography>
        )}
        {evs.map((ev, i) => <EventCard key={i} ev={ev} onClick={onEventClick} />)}
      </Box>
    </Box>
  );
};

// ── Week view (Mon–Sun) ──
export const WeekView: React.FC<{ rows: any[]; weekStart: Dayjs; onEventClick: (e: React.MouseEvent<HTMLElement>, row: any) => void }> = ({ rows, weekStart, onEventClick }) => {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")), [weekStart]);
  const height = (DAY_END - DAY_START) * PX_PER_MIN;
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <Box sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: "12px", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "grid", gridTemplateColumns: "56px repeat(7, 1fr)", borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
        <Box />
        {days.map((d) => {
          const isToday = d.format("YYYY-MM-DD") === today;
          return (
            <Box key={d.toString()} sx={{ textAlign: "center", py: 0.75, bgcolor: isToday ? alpha("#3b82f6", 0.08) : "transparent" }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary" }}>{d.format("ddd")}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: isToday ? "#3b82f6" : "text.primary" }}>{d.format("D")}</Typography>
            </Box>
          );
        })}
      </Box>
      {/* Body */}
      <Box sx={{ display: "grid", gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <Box sx={{ position: "relative", height, bgcolor: alpha("#0f172a", 0.015) }}><HourGrid showLabels /></Box>
        {days.map((d) => {
          const dayRows = rows.filter((r) => dateKey(r) === d.format("YYYY-MM-DD"));
          const evs = layout(dayRows);
          return (
            <Box key={d.toString()} sx={{ position: "relative", height, borderLeft: "1px solid rgba(15,23,42,0.06)" }}>
              <HourGrid />
              {evs.map((ev, i) => <EventCard key={i} ev={ev} compact onClick={onEventClick} />)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
